"use client";

// Handled by hand rather than with a form action: React 19 empties uncontrolled fields after an
// action runs, which would wipe everything a hotel owner typed the moment one field was refused.
import { useRef, useState, useTransition } from "react";
import { CircleCheck, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { sendEnquiryAction } from "@/modules/site/actions";

const ROOMS = ["Under 20", "20 to 50", "51 to 100", "Over 100"];

export function ContactForm() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, start] = useTransition();
  // How long the form has been open: a person takes seconds, and a script takes none.
  const openedAt = useRef(0);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const text = (name: string) => String(fd.get(name) ?? "");
    setError(null);
    start(async () => {
      const r = await sendEnquiryAction({
        name: text("name"), hotel: text("hotel"), mobile: text("mobile"), email: text("email"), city: text("city"), rooms: text("rooms"), message: text("message"),
        website: text("website"), elapsedMs: openedAt.current ? Date.now() - openedAt.current : 0,
      });
      if (r.ok) setSent(true);
      else setError(r.error);
    });
  }

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl bg-card p-10 text-center ring-1 ring-foreground/10" data-enquiry-sent>
        <CircleCheck className="size-10 text-primary" aria-hidden />
        <h2 className="mt-4 text-xl font-semibold">Thank you</h2>
        <p className="mt-2 text-muted-foreground">We have your details and will call you back.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} onFocus={() => { openedAt.current ||= Date.now(); }} className="grid gap-4 rounded-2xl bg-card p-6 ring-1 ring-foreground/10 sm:grid-cols-2 sm:p-8">
      <Field label="Your name" name="name" required autoComplete="name" />
      <Field label="Hotel name" name="hotel" required autoComplete="organization" />
      <Field label="Mobile number" name="mobile" type="tel" required inputMode="tel" autoComplete="tel" />
      <Field label="Email" name="email" type="email" autoComplete="email" />
      <Field label="City" name="city" autoComplete="address-level2" />
      <div className="space-y-1.5">
        <Label htmlFor="rooms">Rooms</Label>
        <select id="rooms" name="rooms" defaultValue="" className="h-10 w-full rounded-lg border border-input bg-background px-2.5 text-base dark:bg-input/30">
          <option value="">Choose</option>
          {ROOMS.map((r) => <option key={r}>{r}</option>)}
        </select>
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="message">What would you like to know?</Label>
        <Textarea id="message" name="message" rows={4} maxLength={1000} className="text-base" />
      </div>
      {/* Left empty by people, who never see it. Automatic form-fillers tend to fill it. */}
      <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" /></label>
      </div>
      {error && <p role="alert" data-enquiry-error className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900 sm:col-span-2 dark:bg-rose-500/10 dark:text-rose-200">{error}</p>}
      <div className="sm:col-span-2">
        <Button type="submit" size="lg" className="h-12 w-full px-6 text-base sm:w-auto" disabled={pending}>
          {pending && <LoaderCircle className="animate-spin" />} Send
        </Button>
        <p className="mt-3 text-sm text-muted-foreground">We use these details only to reply to you.</p>
      </div>
    </form>
  );
}

function Field({ label, name, required, ...props }: { label: string; name: string; required?: boolean } & React.ComponentProps<typeof Input>) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={name}>{label}{required && <span className="text-rose-600" aria-hidden> *</span>}</Label>
      <Input id={name} name={name} required={required} className="h-10 text-base" {...props} />
    </div>
  );
}
